import * as React from "react";
import {useEffect} from "react";
import {translate} from "../../translate/translate";
import {
    Box,
    Button,
    Fab,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {Add, Search} from "@mui/icons-material";
import { URLS } from "../../services/app-urls";
import { PaginatedResult } from "../../models/default/paginated-result";
import TableActionComponent from "../../components/table/table-action.component";
import {ErrorMessage, Field, Form, Formik, FormikHelpers} from "formik";
import {ErrorComponent} from "../../components/error/error.component";
import GenericDialog from "../../components/dialog/generic-dialog.component";
import * as Yup from "yup";
import {PartNumber, Project, statusList} from "../../models/basic/project";
import Select from "@mui/material/Select";
import {Client} from "../../models/basic/client";
import AddItemListComponent from "../../components/list/add-item-list.component";
import {SelectItem} from "../../models/basic/select-item";
import {ProjectFilter, ProjectService} from "../../services/project-service";
import {useFilters} from "../../filters/base-filter";
import {toast} from "react-toastify";
import {Lob} from "../../models/basic/asset-control";
import {AbstractService} from "../../services/abstract-service";
import {usePagination} from "../../utils/pagination";
import {Category} from "../../models/basic/category";
import CollapsibleTableComponent from "../../components/table/collapsible-table.component";
import {useAuth} from "../../contexts/auth-context";
import {Profile} from "../../models/account/user";
import {transformCurrency, transformNumber} from "../../utils/format-number";


const ProjectsComponent = () => {
    const { user } = useAuth();
    const hasAdminPermission = user?.perfil === Profile.ADMIN
    const service = new ProjectService();
    const serviceClient = new AbstractService<Client>(URLS.CLIENT);
    const serviceLob = new AbstractService<Lob>(URLS.LOB);
    const expands = ["client", "lob"];

    const url = "/registration/project/";
    const [dataSourceClient, setDataSourceClient] = React.useState<Client[]>([]);
    const [dataSourceLob, setDataSourceLob] = React.useState<Lob[]>([]);
    const [projectsList, setProjectsList] =
        React.useState<PaginatedResult<Project>>();

    const {filters, updateFilter, updateFilterKeepingParam, resetFilters } =
        useFilters<ProjectFilter>({ name: "", active: "0", client: "" }, ["name", "active", "client"]);

    const [object, setObject] = React.useState(new Project());
    const [openDialog, setOpenDialog] = React.useState(false);
    const [invalidForm, setInvalidForm] = React.useState(true);
    const [partNumberList, setPartNumberList] = React.useState<PartNumber[]>([]);
    const [isEditing, setIsEditing] = React.useState(false);

    const schema = Yup.object().shape({
        name: Yup.string()
            .required(),
        description: Yup.string().required(),
    });

    useEffect(() => {
        loadClients();
        loadLobs();
    }, []);

    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<Category>(projectsList);

    const handleOpenEdit = (row: Project) => {
        if(row.lob === undefined || row.lob === null) {
            row.lob = new Lob();
            row.lob.id = 0;
        }

        row.formatted_budget = transformCurrency(row.budget || 0);
        setIsEditing(true);
        setObject(row);
        checkForm(row);
        handleOpenDialog();
    }

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const checkForm = (row: Project) => {
        setInvalidForm(!row.name.trim() || !row.client.id || row.client.id.toString() === '0' || !row.lob?.id || row.lob?.id.toString() === '0' || !row.budget);
    }

    const handleCloseDialog = () => {
        setIsEditing(false);
        setInvalidForm(true);
        setObject(new Project());
        setOpenDialog(false);
    };

    const loadClients = () => {
        serviceClient.readAll().then(response => {
            setDataSourceClient(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const loadLobs = () => {
        serviceLob.readAll().then(response => {
            setDataSourceLob(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const search = (restartIndex = false) => {
        service.readPaginated(filters, expands, pagination).then(response => {
            setProjectsList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        });

        restartIndex && resetPage();
    };

    useEffect(() => {
        search();
    }, [pagination]);

    const handleDelete = (row: Project) => {
        service.delete(Number(row.id)).then(() => {
            toast.success(translate.t("successfully_deleted"));
            resetFilters();
            resetPage();
            search();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };

    const handleSwitch = (row: Project, column: keyof Project) => {
        (row[column] as boolean) = !row[column];
        const payload = {"active": row.active};
        service.patch(Number(row.id), payload).then(() => {
            toast.success(translate.t("successfully_updated"));
            search();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };

    const handleSubmit = (values: Project) => {
        setTimeout(() => {
            const project = new Project();
            Object.assign(project, values);
            project.part_numbers = removeEmptyPartNumbers(partNumberList);
            project.client = values.client.id as any;
            project.lob = values.lob?.id as any;
            if (project.id !== undefined) {
                service.patch(project.id, project).then(() => {
                    toast.success(translate.t("successfully_updated"));
                    setObject(new Project());
                    handleCloseDialog();
                    search(true);
                }).catch((error: any) => {
                    ErrorComponent(error);
                });
            } else {
                const { id: _, ...newObj } = project;
                service.create(newObj as any)
                    .then(() => {
                        toast.success(translate.t("successfully_registered"));
                        setObject(new Project());
                        handleCloseDialog();
                        search(true);
                    })
                    .catch((error: any) => {
                        ErrorComponent(error);
                    });
            }
        }, 500);
    }

    const handleObjChange = (values: Project) => {
        setObject(values);
        checkForm(values);
    }

    const childrenComponent = (row: Project, key: keyof Project) => {
        const items = row[key];

        if (!Array.isArray(items)) {
            return null;
        }

        return (
            <Box padding="1rem">
                <Grid display="flex" gap={5} flexWrap="wrap">
                    {items.map((item: PartNumber, index: number) => (
                        <Tooltip title={item.amortize ? '' : 'Amortização de Valores NRE desabilitada'} placement="top">
                            <Typography variant="body1"
                                        color={item.amortize ? "secondary" : "#cdcdcd"}
                                        fontWeight={item.amortize ? "bold" : "regular"}
                                        key={index}>
                                {item.part_number}
                            </Typography>
                        </Tooltip>
                    ))}
                </Grid>
            </Box>
        );
    };

    const removeEmptyPartNumbers = (list: PartNumber[]) => {
        return list.filter((partNumber: PartNumber) => partNumber.part_number);
    }

    const hasEmptyPartNumbers = () => {
        return partNumberList.some((partNumber: PartNumber) => !partNumber.part_number) || !partNumberList.length;
    }

    const changePartNumberList = (list: PartNumber[]) => {
        setPartNumberList(list);
    }

    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("projects")}
                </Typography>
            </div>
            <div className="floating-button">
                <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<Add/>}
                    onClick={() => {
                        handleOpenDialog();
                    }}>{translate.t("new_project").toUpperCase()}</Button>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                setTimeout(() => {
                                    search(true);
                                    setSubmitting(false);
                                }, 500);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Grid alignItems="center" container spacing={2} direction={"row"} width="70%">
                                            <Grid size={3}>
                                                <Grid >
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("client")}
                                                        </InputLabel>
                                                        <Select
                                                            name="client"
                                                            label={translate.t("client")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={filters.client}
                                                            onChange={(event) => updateFilterKeepingParam("client", event.target.value)}
                                                        >
                                                            <MenuItem key="0" value="0">
                                                                {translate.t('all')}
                                                            </MenuItem>
                                                            {dataSourceClient?.map((client: Client) => (
                                                                <MenuItem key={client.id} value={client.id}>
                                                                    {client.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                            <Grid size={2}>
                                                <FormControl fullWidth>
                                                    <InputLabel>
                                                        {translate.t("status")}
                                                    </InputLabel>
                                                    <Select
                                                        name="status"
                                                        label={translate.t("status")}
                                                        variant={"outlined"}
                                                        fullWidth
                                                        autoFocus={false}
                                                        value={filters.active}
                                                        onChange={(event) => updateFilterKeepingParam("active", event.target.value)}
                                                    >
                                                        {statusList?.map((stts: SelectItem) => (
                                                            <MenuItem key={stts.value} value={stts.value}>
                                                                {translate.t(stts.label)}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid size={5}>
                                                <Field
                                                    as={TextField}
                                                    name="name"
                                                    label={translate.t("search_project")}
                                                    fullWidth
                                                    variant={"outlined"}
                                                    autoFocus={true}
                                                    value={filters.name}
                                                    onChange={(event: React.ChangeEvent<{
                                                        value: unknown
                                                    }>) => updateFilterKeepingParam("name", event.target.value)}
                                                />
                                            </Grid>
                                            <Grid >
                                                <Fab
                                                    size="small"
                                                    type="submit"
                                                    color="secondary">
                                                    <Search/>
                                                </Fab>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <CollapsibleTableComponent
                    state={dataSource}
                    displayedColumnsShow={["client", "project_platform", "LOB", "Budget", "Status", "action"]}
                    displayedColumns={["client.name", "name", "lob.name", "budget", "active", "action"]}
                    numberColumns={["budget"]}
                    currencyColumns={["budget"]}
                    handleDelete={handleDelete}
                    handleSwitch={handleSwitch}
                    editWithDialog={handleOpenEdit}
                    url={url}
                    handleDoubleClick={handleOpenEdit}
                    count={projectsList?.count}
                    next={projectsList?.next}
                    previous={projectsList?.previous}
                    page={pagination.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.limit}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    actionsComponent={TableActionComponent}
                    columnStyles={{1:"25%", 2:"30%", 3:"10%", 4:"15%", 5:"10%", 6:"10%"}}
                    showActions={["edit", "delete"]}
                    childrenObject={"part_numbers"}
                    childrenComponent={childrenComponent}
                />
			  
            </Paper>
            <GenericDialog
                open={openDialog}
                maxWidth="md"
                onClose={handleCloseDialog}
                title={isEditing ? "edit_project" : "new_project"}
                colorButton={"primary"}
                labelButton={"save"}
                disableConfirm={invalidForm || hasEmptyPartNumbers()}
                handleConfirm={() => handleSubmit(object)}>
                <Box>
                    <FormControl fullWidth>
                        <Formik
                            id="dialog-form"
                            enableReinitialize
                            initialValues={object}
                            validationSchema={schema}
                            onSubmit={(values: Project, {resetForm}: FormikHelpers<Project>) => {
                                handleSubmit(values);
                                resetForm();
                                handleCloseDialog();
                            }}
                            validateOnChange={true}>
                            {({isValid, values, isValidating, errors, touched}) => (
                                <Form autoComplete="off">
                                    <Grid container spacing={2} direction={"column"}>
                                        <Grid >
                                            <Field
                                                as={TextField}
                                                name="name"
                                                label={translate.t("project_name")}
                                                variant={"outlined"}
                                                fullWidth
                                                autoFocus={true}
                                                required={true}
                                                values={values}
                                                error={touched.name && Boolean(errors.name) && invalidForm}
                                                onChange={(event: React.ChangeEvent<{
                                                    value: unknown
                                                }>) => {
                                                    if (event.target.value && event.target.value.toString().length > 255) {
                                                        return;
                                                    }
                                                    const obj = object;
                                                    const value = event.target.value as string;
                                                    handleObjChange({
                                                        ...obj,
                                                        name: value as string,
                                                    });
                                                }}
                                                InputProps={{
                                                    maxLength: 54,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {touched.name && Boolean(errors.name) && invalidForm && (
                                                                <div className={"required-field"}>
                                                                    {translate.t("required_field")}
                                                                </div>
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}/>
                                        </Grid>

                                        <Grid >
                                            <Grid container spacing={2} direction={"row"}>
                                                <Grid size={6}>
                                                    <FormControl fullWidth>
                                                        <InputLabel
                                                            required={true}
                                                            className={
                                                                touched.client && Boolean(errors.client) ? "error-required" : ""
                                                            }
                                                        >
                                                            {translate.t("client")}
                                                        </InputLabel>
                                                        <Field
                                                            as={Select}
                                                            name="client.id"
                                                            label={translate.t("client")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            values={values}
                                                            placeholder={translate.t("select_client")}
                                                            error={touched.client && Boolean(errors.client)}
                                                            helperText={<ErrorMessage name="client"/>}
                                                            onChange={(event: React.ChangeEvent<{
                                                                value: number
                                                            }>) => {
                                                                const obj = object;
                                                                const value = event.target.value;
                                                                handleObjChange({
                                                                    ...obj,
                                                                    client: {
                                                                        ...obj.client,
                                                                        id: value,
                                                                    },
                                                                });
                                                            }}
                                                            required={true}>
                                                            <MenuItem key="0" value="0">
                                                                {translate.t("select_client")}
                                                            </MenuItem>
                                                            {dataSourceClient?.map((client: Client) => (
                                                                <MenuItem key={client.id} value={client.id}>
                                                                    {client.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Field>
                                                        {(touched.client && Boolean(errors.client)) && (
                                                            <div className={"required-field"}>
                                                                {translate.t("required_field")}
                                                            </div>
                                                        )}
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormControl fullWidth>
                                                        <InputLabel
                                                            required={true}
                                                            className={
                                                                touched.lob && Boolean(errors.lob) ? "error-required" : ""
                                                            }
                                                        >
                                                            LOB (Line of Business)
                                                        </InputLabel>
                                                        <Field
                                                            as={Select}
                                                            name="lob.id"
                                                            label={"LOB (Line of Business)"}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            values={values}
                                                            placeholder={translate.t("select_lob")}
                                                            error={touched.lob && Boolean(errors.lob)}
                                                            helperText={<ErrorMessage name="lob"/>}
                                                            onChange={(event: React.ChangeEvent<{
                                                                value: number
                                                            }>) => {
                                                                const obj = object;
                                                                const value = event.target.value;
                                                                handleObjChange({
                                                                    ...obj,
                                                                    lob: {
                                                                        ...obj.lob,
                                                                        id: value,
                                                                    },
                                                                });
                                                            }}
                                                            required={true}>
                                                            <MenuItem key="0" value="0">
                                                                {translate.t("select_lob")}
                                                            </MenuItem>
                                                            {dataSourceLob?.map((lob: Lob) => (
                                                                <MenuItem key={lob.id} value={lob.id}>
                                                                    {lob.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Field>
                                                        {(touched.lob && Boolean(errors.lob)) && (
                                                            <div className={"required-field"}>
                                                                {translate.t("required_field")}
                                                            </div>
                                                        )}
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid >
                                            <Field
                                                as={TextField}
                                                name="formatted_budget"
                                                label={'Budget R$'}
                                                variant={"outlined"}
                                                fullWidth
                                                autoFocus={false}
                                                required={true}
                                                values={values}
                                                error={touched.budget && Boolean(errors.budget) && invalidForm}
                                                onChange={(event: React.ChangeEvent<{
                                                    value: string
                                                }>) => {
                                                    const obj = object;
                                                    let rawValue = event.target.value;

                                                    if (rawValue && rawValue.length > 22) {
                                                        return;
                                                    }

                                                    let numericValue = transformNumber(rawValue);
                                                    handleObjChange({
                                                        ...obj,
                                                        budget: numericValue,
                                                        formatted_budget: transformCurrency(numericValue),
                                                    });
                                                }}
                                                InputProps={{
                                                    maxLength: 54,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {touched.budget && Boolean(errors.budget) && (
                                                                <div className={"required-field"}>
                                                                    {translate.t("required_field")}
                                                                </div>
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>

                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    </FormControl>
                    <AddItemListComponent list={object.part_numbers || []}
                                          field={"part_number"}
                                          changeList={changePartNumberList}
                                          title={translate.t("part_numbers_register")}
                                          hasSwitch={hasAdminPermission}
                                          switchField={"amortize"}
                                          switchLabel={"Amortizar Valores NRE"}
                    />
                </Box>
            </GenericDialog>
        </section>
    );
};

export default ProjectsComponent;
